// =============================================================================
// TrackerSettings.jsx
//
// Dialog de gestão de lookups do Tracker.
// Acessível apenas a Admin e Manager.
//
// Lookups: Technology · Vendor · Type (Feature) · Team · Activity Owner
// Cada lookup tem uma tabela com Add / Edit inline / Delete
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Box, Typography, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, Switch, FormControlLabel, MenuItem, CircularProgress,
  Tooltip, Alert, Chip,
} from '@mui/material';
import CloseIcon        from '@mui/icons-material/Close';
import AddIcon          from '@mui/icons-material/Add';
import EditIcon         from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon         from '@mui/icons-material/Save';
import CancelIcon       from '@mui/icons-material/Cancel';
import AxiosRequest from '../../../utils/AxiosRequest.js';
import config      from '../../../configuration/configuration.js';

const BASE = `${config.API_URL}TrackerSettings`;

// ── API helpers ───────────────────────────────────────────────────────────────
const api = {
  get:    (path)       => AxiosRequest({ method: 'GET',    url: `${BASE}/${path}` }),
  save:   (path, data) => AxiosRequest({ method: 'POST',   url: `${BASE}/${path}`, data }),
  delete: (path, id)   => AxiosRequest({ method: 'DELETE', url: `${BASE}/${path}/${id}` }),
};

// ── Lookup tabs config ────────────────────────────────────────────────────────
const TABS = [
  { key: 'Technologies', label: 'Technology',      hasTeam: false },
  { key: 'Vendors',      label: 'Vendor',          hasTeam: false },
  { key: 'Features',     label: 'Type',            hasTeam: false },
  { key: 'Teams',        label: 'Team',            hasTeam: false },
  { key: 'Owners',       label: 'Activity Owner',  hasTeam: true  },
];

// ── Empty row factories ───────────────────────────────────────────────────────
const emptyRow     = ()         => ({ id: null, name: '', isActive: true, sortOrder: 0 });
const emptyOwner   = ()         => ({ id: null, name: '', teamId: '', isActive: true, sortOrder: 0 });

// =============================================================================
// LookupTable — tabela genérica para um lookup
// =============================================================================
const LookupTable = ({ tabKey, hasTeam, teams }) => {
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [editId,     setEditId]     = useState(null);
  const [editForm,   setEditForm]   = useState(null);
  const [teamFilter, setTeamFilter] = useState('');  // '' = all teams

  // Rows filtered by selected team (only for Owners tab)
  const filteredRows = hasTeam && teamFilter
    ? rows.filter((r) => String(r.teamId) === teamFilter)
    : rows;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get(tabKey)
      .then((res) => setRows(res.data ?? []))
      .catch(() => setError('Failed to load data.'))
      .finally(() => setLoading(false));
  }, [tabKey]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => {
    setEditId(row.id ?? 'new');
    setEditForm({ ...row, teamId: row.teamId ? String(row.teamId) : '' });
  };

  const cancelEdit = () => { setEditId(null); setEditForm(null); };

  const handleSave = () => {
    if (!editForm.name?.trim()) return;
    setSaving(true);
    setError(null);

    const payload = hasTeam
      ? { ...editForm, id: editId === 'new' ? null : editId, teamId: Number(editForm.teamId) }
      : { ...editForm, id: editId === 'new' ? null : editId };

    const endpoint = tabKey;
    api.save(endpoint, payload)
      .then(() => { cancelEdit(); load(); })
      .catch(() => setError('Failed to save.'))
      .finally(() => setSaving(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Deactivate this item? It will no longer appear in dropdowns.')) return;
    setSaving(true);
    api.delete(tabKey, id)
      .then(() => load())
      .catch(() => setError('Failed to delete.'))
      .finally(() => setSaving(false));
  };

  const handleAddNew = () => {
    startEdit(hasTeam ? emptyOwner() : emptyRow());
  };

  const cellSx = { fontSize: '0.78rem', py: 0.75 };
  const headSx = { fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase',
                   letterSpacing: 0.8, color: 'rgba(255,255,255,0.85)', bgcolor: 'primary.main',
                   py: 1, borderBottom: 'none' };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {/* ── Team filter — só no tab Owners ── */}
      {hasTeam && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            select size="small" label="Filter by Team"
            value={teamFilter}
            onChange={(e) => { setTeamFilter(e.target.value); cancelEdit(); }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value=""><em>All Teams</em></MenuItem>
            {teams.map((t) => (
              <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
            ))}
          </TextField>
          {teamFilter && (
            <Typography variant="caption" color="text.secondary">
              {filteredRows.length} owner{filteredRows.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
      )}

      <TableContainer sx={{ maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {hasTeam && <TableCell sx={{ ...headSx, bgcolor: 'primary.main' }}>Team</TableCell>}
              <TableCell sx={{ ...headSx, bgcolor: 'primary.main' }}>Name</TableCell>
              <TableCell sx={{ ...headSx, bgcolor: 'primary.main', width: 70 }}>Order</TableCell>
              <TableCell sx={{ ...headSx, bgcolor: 'primary.main', width: 80 }}>Active</TableCell>
              <TableCell sx={{ ...headSx, bgcolor: 'primary.main', width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={hasTeam ? 5 : 4} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={hasTeam ? 5 : 4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {teamFilter ? 'No owners for this team.' : 'No items yet.'}
                </TableCell>
              </TableRow>
            ) : filteredRows.map((row) => {
              const isEditing = editId === row.id;
              return (
                <TableRow key={row.id} sx={{ opacity: row.isActive ? 1 : 0.45 }}>
                  {hasTeam && (
                    <TableCell sx={cellSx}>
                      {isEditing ? (
                        <TextField select size="small" value={editForm.teamId}
                          onChange={(e) => setEditForm((p) => ({ ...p, teamId: e.target.value }))}
                          sx={{ minWidth: 140 }}>
                          {teams.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                        </TextField>
                      ) : (
                        teams.find((t) => t.id === row.teamId)?.name ?? '—'
                      )}
                    </TableCell>
                  )}
                  <TableCell sx={cellSx}>
                    {isEditing ? (
                      <TextField size="small" value={editForm.name} autoFocus
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') cancelEdit(); }}
                        sx={{ minWidth: 200 }} />
                    ) : (
                      <Typography variant="body2" fontWeight={row.isActive ? 500 : 400}>
                        {row.name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {isEditing ? (
                      <TextField size="small" type="number" value={editForm.sortOrder}
                        onChange={(e) => setEditForm((p) => ({ ...p, sortOrder: Number(e.target.value) }))}
                        sx={{ width: 60 }} />
                    ) : row.sortOrder}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {isEditing ? (
                      <Switch size="small" checked={editForm.isActive}
                        onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))} />
                    ) : (
                      <Chip label={row.isActive ? 'Active' : 'Inactive'} size="small"
                        color={row.isActive ? 'success' : 'default'}
                        sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Save">
                          <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}>
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelEdit} disabled={saving}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => startEdit(row)} disabled={saving || editId !== null}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deactivate">
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)} disabled={saving || editId !== null}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {/* New row inline */}
            {editId === 'new' && (
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {hasTeam && (
                  <TableCell sx={cellSx}>
                    <TextField select size="small" value={editForm.teamId}
                      onChange={(e) => setEditForm((p) => ({ ...p, teamId: e.target.value }))}
                      sx={{ minWidth: 140 }}>
                      {teams.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
                    </TextField>
                  </TableCell>
                )}
                <TableCell sx={cellSx}>
                  <TextField size="small" value={editForm.name} autoFocus
                    placeholder="Name…"
                    onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') cancelEdit(); }}
                    sx={{ minWidth: 200 }} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <TextField size="small" type="number" value={editForm.sortOrder}
                    onChange={(e) => setEditForm((p) => ({ ...p, sortOrder: Number(e.target.value) }))}
                    sx={{ width: 60 }} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <Switch size="small" checked={editForm.isActive}
                    onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Save">
                      <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}>
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton size="small" onClick={cancelEdit} disabled={saving}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1.5 }}>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={handleAddNew} disabled={editId !== null || loading}>
          Add {TABS.find((t) => t.key === tabKey)?.label}
        </Button>
      </Box>
    </Box>
  );
};

// =============================================================================
// TrackerSettings — Dialog principal
// =============================================================================
const TrackerSettings = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState('Technologies');
  const [teams,     setTeams]     = useState([]);

  // Load teams once — needed for Owner dropdown
  // Uses the public tracker endpoint (not the settings one) to avoid auth issues
  useEffect(() => {
    if (!open) return;
    AxiosRequest({ method: 'GET', url: `${config.API_URL}Tracker/GetTeams` })
      .then((res) => setTeams(res.data ?? []))
      .catch(() => {});
  }, [open]);

  const currentTab = TABS.find((t) => t.key === activeTab);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography fontWeight={700} fontSize="1.05rem">Tracker Settings</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {/* ── Tab selector ── */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          {TABS.map((tab) => (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                px: 2, py: 0.6, borderRadius: 2, cursor: 'pointer',
                fontSize: '0.8rem', fontWeight: 600,
                bgcolor: activeTab === tab.key ? 'primary.main' : 'action.hover',
                color:   activeTab === tab.key ? 'white' : 'text.primary',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: activeTab === tab.key ? 'primary.dark' : 'action.selected' },
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Box>

        {/* ── Active lookup table ── */}
        <LookupTable
          key={activeTab}
          tabKey={activeTab}
          hasTeam={currentTab?.hasTeam ?? false}
          teams={teams}
        />
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrackerSettings;